import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";

// Internal
import { API_ENTRYPOINT } from "@/utils/fetch";
import { httpRequests } from "./_shared";
import { IPayment } from "@/types/payment.types";
import { MIN_DATE_THRESHOLD } from "./attendance";

interface GetPaymentsListDto {
  startDate: string;
  endDate: string;
  familyId?: string | number;
  providerId?: string | number;
}

// QUERY KEYS
export const paymentsQueryKeys = {
  all: ["payments"] as const,
  lists: () => [...paymentsQueryKeys.all, "list"] as const,
  list: (dto: GetPaymentsListDto) =>
    [...paymentsQueryKeys.lists(), dto] as const,
};

/*
 * Get payments list (batched payments for the selected release-date range)
 */
export const _getPaymentsList = async ({
  startDate,
  endDate,
  familyId,
  providerId,
}: GetPaymentsListDto) => {
  return httpRequests.get<IPayment[]>(
    `${API_ENTRYPOINT}/${providerId ? "providers" : "families"}/${
      providerId || familyId
    }/payments?start=${startDate}&end=${endDate}`,
  );
};

export const useGetPaymentsList = (
  dto: GetPaymentsListDto & { enabled?: boolean },
) => {
  const { enabled = true, ...queryDto } = dto;

  return useQuery<IPayment[], Error>({
    queryKey: paymentsQueryKeys.list(queryDto),
    queryFn: async () => {
      const data = await _getPaymentsList(queryDto);
      return data;
    },
    enabled:
      enabled &&
      !!queryDto.startDate &&
      !!queryDto.endDate &&
      !!(queryDto.providerId || queryDto.familyId) &&
      dayjs(queryDto.endDate).isAfter(dayjs(MIN_DATE_THRESHOLD)),
    retry: 1,
  });
};
